import type { RatingPayload, UsageData } from '@ctct/infrastructure-layer';

import { RatingRequestCustomer } from './rating-request-customer';
import { FE_ADD_ON_ID } from '../add-ons';

export type RatingCallRequestConstructorParams = Omit<RatingPayload, 'customer'> & {
  customer: RatingRequestCustomer;
};

export class RatingCallRequest {
  public static fromJson(json: RatingPayload): RatingCallRequest {
    return new RatingCallRequest({
      ...json,
      customer: RatingRequestCustomer.fromJson(json.customer),
    });
  }

  public toJson(): RatingPayload {
    const customerJson = this.values.customer.toJson();

    return {
      includeTax: this.values.includeTax,
      customer: customerJson,
      currencyCode: this.values.currencyCode,
      planGroupId: this.values.planGroupId,
      currentPlanId: this.values.currentPlanId,
      newPlanId: this.values.newPlanId,
      newAddOnServices: this.values.newAddOnServices,
      paymentPeriod: this.values.paymentPeriod,
      paymentPlanId: this.values.paymentPlanId,
      usageData: this.values.usageData,
      existingAddOnServices: this.values.existingAddOnServices,
      includeRemainingBalance: this.values.includeRemainingBalance,
      newPromotionCodes: this.values.newPromotionCodes,
      ...(this.values.stripeAccount ? { stripeAccount: true } : {}),
    };
  }

  constructor(private readonly values: RatingCallRequestConstructorParams) {}

  public withIncludeTax(includeTax: boolean): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      includeTax,
    });
  }

  public withCurrencyCode(currencyCode: string): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      currencyCode,
    }).withCustomer(this.customer.withCurrencyCode(currencyCode));
  }

  public get currencyCode(): string | undefined {
    return this.values.currencyCode;
  }

  public withPlanGroupId(planGroupId: number): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      planGroupId,
    });
  }

  public withCurrentPlanId(currentPlanId: number): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      currentPlanId,
    });
  }

  public get currentPlanId(): number {
    return this.values.currentPlanId;
  }

  public withNewPlanId(newPlanId: number): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      newPlanId,
    });
  }

  public get newPlanId(): number {
    return this.values.newPlanId;
  }

  public withNewAddOnServices(newAddOnServices: string[]): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      newAddOnServices: newAddOnServices.filter((addOn) => addOn !== FE_ADD_ON_ID.XERO),
    });
  }

  public withPaymentPeriod(paymentPeriod: number): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      paymentPeriod,
    });
  }

  public withPaymentPlanId(paymentPlanId: number): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      paymentPlanId,
    });
  }

  public withUsageData(usageData: UsageData[]): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      usageData,
    });
  }

  public get usageData(): UsageData[] {
    return this.values.usageData;
  }

  public withExistingAddOnServices(existingAddOnServices: string[]): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      existingAddOnServices,
    });
  }

  public withIncludeRemainingBalance(includeRemainingBalance: boolean): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      includeRemainingBalance,
    });
  }

  public withNewPromotionCodes(newPromotionCodes: string[]): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      newPromotionCodes,
    });
  }

  public withAddedPromotionCode(promotionCode: string): RatingCallRequest {
    if (this.hasPromotionCode(promotionCode)) {
      return this;
    }

    return new RatingCallRequest({
      ...this.values,
      newPromotionCodes: [...this.values.newPromotionCodes, promotionCode.toUpperCase()],
    });
  }

  public withRemovedPromotionCodes(...codesToRemove: string[]): RatingCallRequest {
    if (!codesToRemove.length) return this;

    // `newPromotionCodes` represents the promo codes that will be applied to the order. When the user clicks "remove"
    // in the UI, we want to remove the promo code from the list. The nomenclature is confusing on the frontend,
    // but makes more sense on the backend.
    const current = this.values.newPromotionCodes;
    const next = current.filter((c) => !codesToRemove.includes(c));

    return next.length === current.length ? this : new RatingCallRequest({ ...this.values, newPromotionCodes: next });
  }

  public hasPromotionCode(promotionCode: string): boolean {
    return this.values.newPromotionCodes.includes(promotionCode.toUpperCase());
  }

  public get newPromotionCodes(): string[] {
    return this.values.newPromotionCodes;
  }

  public withCustomer(customer: RatingRequestCustomer): RatingCallRequest {
    return new RatingCallRequest({
      ...this.values,
      customer,
    });
  }

  public get customer(): RatingRequestCustomer {
    return this.values.customer;
  }

  public withStripeAccount(stripeAccount: boolean): RatingCallRequest {
    if (stripeAccount === Boolean(this.values.stripeAccount)) {
      return this;
    }

    return new RatingCallRequest({
      ...this.values,
      stripeAccount,
    });
  }

  public get stripeAccount(): boolean {
    return this.values.stripeAccount ?? false;
  }
}
